/**
 * VoiceRecorder — self-contained mic control for the Copilot input.
 *
 * States: idle (mic) -> recording (elapsed timer + stop button) ->
 * transcribing (spinner) -> back to idle. Degrades honestly:
 *   - `GET /voice/config` says `enabled:false` -> disabled button, French
 *     tooltip, NEVER a fake transcript.
 *   - Mic permission denied -> inline French error, button stays usable to retry.
 *   - Backend 503 (`voice_unavailable`) -> inline French error, never invents text.
 *   - Backend 402 (out of AI credits) -> NOT handled here: `transcribe()`
 *     (src/lib/api/voice.ts) already let the global insufficient-credits
 *     event fire via the shared API client, so this component just resets
 *     its own local state and does not render a second paywall.
 *
 * Fully self-contained (no dependency on CopilotWidget/copilot-store) — the
 * integration lane wires it in via `onTranscript`.
 */

import { useEffect, useRef, useState } from 'react';

import { ApiError } from '@/lib/api/client';
import {
  getVoiceConfig,
  isVoiceOutOfCredits,
  isVoiceTooLarge,
  isVoiceUnavailable,
  transcribe,
  type VoiceConfig,
} from '@/lib/api/voice';

import { useVoiceRecorder } from './useVoiceRecorder';

export interface VoiceRecorderProps {
  /** Called with the final transcript once transcription succeeds. Prefers the
   * translated text (when `translateTo` was supplied and translation ran). */
  onTranscript: (text: string) => void;
  /** Called on any failure with a French, user-facing message. */
  onError?: (message: string) => void;
  /** ISO language hint for Whisper (improves accuracy). */
  language?: string;
  /** Run the server-side ASR-cleanup pass. Default true. */
  correct?: boolean;
  /** Target language code/name to translate the transcript into. */
  translateTo?: string;
  /** Hard recording ceiling in seconds. Default 60. */
  maxDurationSec?: number;
  /** Extra classes for the wrapping element. */
  className?: string;
  /** Force-disable regardless of server config (e.g. a parent-level gate). */
  disabled?: boolean;
}

function formatElapsed(totalSec: number): string {
  const s = Math.max(0, Math.floor(totalSec));
  const mm = Math.floor(s / 60)
    .toString()
    .padStart(2, '0');
  const ss = (s % 60).toString().padStart(2, '0');
  return `${mm}:${ss}`;
}

function MicIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true">
      <path
        d="M12 15a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3Z"
        stroke="currentColor"
        strokeWidth="1.7"
      />
      <path
        d="M19 11a7 7 0 0 1-14 0M12 18v3"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  );
}

function StopIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
      <rect x="6" y="6" width="12" height="12" rx="2" />
    </svg>
  );
}

function Spinner() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      aria-hidden="true"
      className="animate-spin"
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" opacity="0.25" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

export function VoiceRecorder({
  onTranscript,
  onError,
  language,
  correct = true,
  translateTo,
  maxDurationSec = 60,
  className,
  disabled,
}: VoiceRecorderProps) {
  const [config, setConfig] = useState<VoiceConfig | null>(null);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const handleStopRef = useRef<() => void>(() => {});
  const recorder = useVoiceRecorder({
    maxDurationSec,
    onMaxDurationReached: () => handleStopRef.current(),
  });

  useEffect(() => {
    let alive = true;
    getVoiceConfig().then((cfg) => {
      if (alive) {
        setConfig(cfg);
        setConfigLoaded(true);
      }
    });
    return () => {
      alive = false;
    };
  }, []);

  // Reset our own inline error whenever the recorder reports a fresh one
  // (permission denied / unsupported) so the two never show stale text.
  useEffect(() => {
    if (recorder.error) setLocalError(recorder.error);
  }, [recorder.error]);

  const reportError = (message: string) => {
    setLocalError(message);
    onError?.(message);
  };

  const handleStart = async () => {
    setLocalError(null);
    await recorder.start();
  };

  const handleStop = async () => {
    const recording = await recorder.stop();
    if (!recording || recording.blob.size === 0) {
      reportError('Aucun son capté — réessayez en parlant plus près du micro.');
      return;
    }
    setTranscribing(true);
    setLocalError(null);
    try {
      const result = await transcribe(recording.blob, {
        language,
        correct,
        translate_to: translateTo,
      });
      onTranscript(result.translated_text || result.text);
    } catch (err) {
      if (isVoiceOutOfCredits(err)) {
        // The global psn:ai-credits-insufficient event already fired inside
        // transcribe()/apiFetch — the shared refill modal/toast handles it.
        // No bespoke voice paywall here.
      } else if (isVoiceUnavailable(err)) {
        reportError(err.reason || 'Reconnaissance vocale momentanément indisponible. Réessayez plus tard.');
      } else if (isVoiceTooLarge(err)) {
        reportError(err.message);
      } else if (err instanceof ApiError) {
        const body = err.body as { detail?: unknown } | null;
        const detail = typeof body?.detail === 'string' ? body.detail : undefined;
        reportError(detail || 'La transcription a échoué. Réessayez.');
      } else {
        reportError('La transcription a échoué. Réessayez.');
      }
    } finally {
      setTranscribing(false);
    }
  };
  handleStopRef.current = () => void handleStop();

  const handleCancel = () => {
    recorder.cancel();
    setLocalError(null);
  };

  const voiceDisabledByServer = configLoaded && !config?.enabled;
  const unsupported = recorder.status === 'unsupported';
  const isDisabled = Boolean(disabled) || !configLoaded || voiceDisabledByServer || unsupported || transcribing;

  const tooltip = disabled
    ? undefined
    : !configLoaded
      ? 'Vérification de la disponibilité…'
      : voiceDisabledByServer
        ? 'Reconnaissance vocale non configurée'
        : unsupported
          ? "Enregistrement audio non pris en charge par ce navigateur"
          : recorder.status === 'recording'
            ? `Arrêter l'enregistrement (${formatElapsed(recorder.elapsedSec)})`
            : transcribing
              ? 'Transcription en cours…'
              : 'Saisie vocale';

  const wrapperClass = ['inline-flex items-center gap-2', className].filter(Boolean).join(' ');

  if (recorder.status === 'recording') {
    return (
      <div className={wrapperClass}>
        <button
          type="button"
          onClick={() => void handleStop()}
          title={tooltip}
          aria-label={`Arrêter l'enregistrement vocal, ${formatElapsed(recorder.elapsedSec)} écoulées`}
          className="inline-flex items-center gap-1.5 rounded-token border border-danger/40 bg-danger/10 px-2.5 py-1.5 text-sm font-medium text-danger transition-colors hover:bg-danger/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-danger opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-danger" />
          </span>
          <StopIcon />
          <span className="tabular-nums">{formatElapsed(recorder.elapsedSec)}</span>
        </button>
        <button
          type="button"
          onClick={handleCancel}
          aria-label="Annuler l'enregistrement"
          title="Annuler"
          className="text-xs text-muted hover:text-foreground"
        >
          Annuler
        </button>
      </div>
    );
  }

  return (
    <div className={wrapperClass}>
      <button
        type="button"
        onClick={() => void handleStart()}
        disabled={isDisabled}
        title={tooltip}
        aria-label={transcribing ? 'Transcription en cours' : 'Démarrer la saisie vocale'}
        aria-busy={transcribing}
        className="inline-flex items-center justify-center rounded-token border border-border bg-transparent p-1.5 text-muted transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
      >
        {transcribing ? <Spinner /> : <MicIcon />}
      </button>
      {localError && (
        <p role="alert" className="text-xs text-danger">
          {localError}
        </p>
      )}
    </div>
  );
}
